"use client";

import Link from "next/link";
import type { CompanionRecommendationSport } from "@/lib/companion/recommendation-types";
import { CompanionSportSetup } from "./CompanionSportSetup";
import { CompanionViewAnalytics } from "./CompanionAnalytics";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { CheckCircle2 } from "lucide-react";
import { saveDailyBriefingPreferences } from "@/app/actions/briefing-preferences";
import { addFavourite } from "@/app/actions/favourites";
import { followTheCall } from "@/app/actions/the-call";
import type { CompanionHomeModel } from "@/lib/companion/home-model";
import {
  clearCompanionSignupIntent,
  COMPANION_SIGNUP_INTENT_QUERY_PARAM,
  parseCompanionSignupIntentParam,
  readCompanionSignupIntent,
  resolveCompanionSignupIntentPrefill,
  writeCompanionSignupIntent,
  type CompanionSignupIntentPrefill,
} from "@/lib/companion/signup-intent";
import { CompanionBaseSheet } from "../CompanionBaseSheet";
import {
  buildCompanionIntentSuggestionView,
  companionSetupProgress,
  type CompanionIntentSuggestionView,
} from "../companion-home-state";
import { queueCompanionRefresh } from "./CompanionPlanWorkspace";
import { sportIsBeta } from "@/lib/conditions/sport-meta";
import { SportBetaBadge } from "@/app/_components/conditions/SportBetaBadge";

export function useCompanionSignupIntentPrefill(
  renderedAt: string,
  enabled: boolean,
  includeNamedSupProfiles = false,
  setupIntentParam: string | null = null,
): CompanionSignupIntentPrefill | null {
  const [intentPrefill, setIntentPrefill] =
    useState<CompanionSignupIntentPrefill | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const now = new Date(renderedAt);
    const urlIntent = parseCompanionSignupIntentParam(setupIntentParam, { now });
    const intent = urlIntent ?? readCompanionSignupIntent(window.localStorage, { now });
    if (urlIntent) {
      writeCompanionSignupIntent({
        now: urlIntent.createdAt,
        regionSlug: urlIntent.regionSlug,
        spotSlugs: urlIntent.spotSlugs,
        sports: urlIntent.sports,
        autoSaveSpots: urlIntent.autoSaveSpots,
      });
    }
    const prefill = intent
      ? resolveCompanionSignupIntentPrefill(intent, { includeNamedSupProfiles })
      : null;

    if (
      !prefill ||
      (!prefill.region &&
        prefill.suggestedSpots.length === 0 &&
        prefill.suggestedSports.length === 0)
    ) {
      window.setTimeout(() => setIntentPrefill(null), 0);
      return undefined;
    }

    let cancelled = false;
    window.setTimeout(() => {
      if (!cancelled) setIntentPrefill(prefill);
    }, 0);

    return () => {
      cancelled = true;
    };
  }, [enabled, includeNamedSupProfiles, renderedAt, setupIntentParam]);

  return intentPrefill;
}

export function CompanionSetupContent({
  model,
  intentPrefill,
  includeBaseSheet,
  knownSports,
  includeNamedSupProfiles,
}: {
  model: CompanionHomeModel;
  intentPrefill: CompanionSignupIntentPrefill | null;
  includeBaseSheet: boolean;
  knownSports?: CompanionRecommendationSport[];
  includeNamedSupProfiles?: boolean;
}): ReactNode {
  const setupProgress = companionSetupProgress(model);

  return (
    <div id="companion-setup" className="scroll-mt-24">
      {knownSports ? <CompanionViewAnalytics key={`${Boolean(model.activeBase)}:${knownSports.length}`} event={{ name: "companion_setup_progress", properties: { has_base: Boolean(model.activeBase), has_sports: knownSports.length > 0 } }} /> : null}
      {includeBaseSheet ? (
        <CompanionBaseSheet
          key={intentPrefill?.region?.slug ?? "base-sheet"}
          model={model}
          intentPrefill={intentPrefill}
          triggerTone="light"
          navigationMode="route"
        />
      ) : null}
      {setupProgress.isComplete && knownSports === undefined ? (
        <SetupCompleteStrip
          progress={setupProgress}
          confirmation={model.readyConfirmation}
        />
      ) : (
        <FirstRunChecklist
          steps={model.checklist}
          knownSports={knownSports}
          includeNamedSupProfiles={includeNamedSupProfiles}
          confirmation={model.readyConfirmation}
          intentPrefill={intentPrefill}
        />
      )}
    </div>
  );
}

export function CompanionSetupPanel({
  model,
  knownSports,
  renderedAt,
  includeBaseSheet = true,
  includeNamedSupProfiles = false,
  setupIntentParam = null,
}: {
  model: CompanionHomeModel;
  knownSports?: CompanionRecommendationSport[];
  renderedAt: string;
  includeBaseSheet?: boolean;
  includeNamedSupProfiles?: boolean;
  setupIntentParam?: string | null;
}): ReactNode {
  const intentPrefill = useCompanionSignupIntentPrefill(
    renderedAt,
    true,
    includeNamedSupProfiles,
    setupIntentParam,
  );

  return (
    <CompanionSetupContent
      model={model}
      intentPrefill={intentPrefill}
      includeBaseSheet={includeBaseSheet}
      knownSports={knownSports}
      includeNamedSupProfiles={includeNamedSupProfiles}
    />
  );
}

export function CompanionSignupIntentPanel({
  renderedAt,
  includeNamedSupProfiles = false,
  setupIntentParam = null,
}: {
  renderedAt: string;
  includeNamedSupProfiles?: boolean;
  setupIntentParam?: string | null;
}): ReactNode {
  const intentPrefill = useCompanionSignupIntentPrefill(
    renderedAt,
    true,
    includeNamedSupProfiles,
    setupIntentParam,
  );
  const view = buildCompanionIntentSuggestionView(intentPrefill);
  return view ? <IntentSuggestions view={view} /> : null;
}

function SetupCompleteStrip({
  progress,
  confirmation,
}: {
  progress: ReturnType<typeof companionSetupProgress>;
  confirmation: CompanionHomeModel["readyConfirmation"];
}) {
  return (
    <section
      aria-label="Companion setup status"
      className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-[13px] font-semibold text-emerald-950">
              Companion active
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-emerald-900">
              {progress.completed}/{progress.total} setup steps complete.{" "}
              {confirmation?.body ??
                "Your saved places and tracking are ready when you need them."}
            </p>
          </div>
        </div>
        <Link
          href={confirmation?.href ?? "/conditions"}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-sm border border-emerald-300 bg-white px-3 py-2 text-[12px] font-semibold text-emerald-950 no-underline hover:border-emerald-500"
        >
          Review places
        </Link>
      </div>
    </section>
  );
}

function FirstRunChecklist({
  steps,
  knownSports,
  includeNamedSupProfiles,
  confirmation,
  intentPrefill,
}: {
  steps: CompanionHomeModel["checklist"];
  knownSports?: CompanionRecommendationSport[];
  includeNamedSupProfiles?: boolean;
  confirmation: CompanionHomeModel["readyConfirmation"];
  intentPrefill: CompanionSignupIntentPrefill | null;
}) {
  const checklistSteps = knownSports === undefined
    ? steps
    : steps.map((step) => step.id === "follow-sports"
      ? { ...step, done: knownSports.length > 0 }
      : step);
  const router = useRouter();
  const intentSuggestion = buildCompanionIntentSuggestionView(intentPrefill);
  const [briefingStatus, setBriefingStatus] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const [briefingPending, startBriefingTransition] = useTransition();
  const saveInAppOnlyBriefing = useCallback(() => {
    setBriefingStatus(null);
    startBriefingTransition(() => {
      void (async () => {
        const result = await saveDailyBriefingPreferences({
          briefingEnabled: true,
          localSendTime: "06:00",
          emailBriefingConsent: false,
          pushBriefingConsent: false,
        });

        if (!result.success) {
          setBriefingStatus({ tone: "error", message: result.error });
          return;
        }

        setBriefingStatus({ tone: "success", message: "In-app briefing saved." });
        queueCompanionRefresh(router);
      })();
    });
  }, [router]);

  return (
    <section
      aria-labelledby="checklist-heading"
      className="rounded-md border border-border-light bg-white p-4 md:p-5"
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
            First run
          </p>
          <h2
            id="checklist-heading"
            className="font-serif text-[26px] leading-tight text-navy"
          >
            Your first useful session
          </h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
            Choose a base and your sports to see today’s forecast. Free time and morning Briefings are optional.
          </p>
        </div>
        <span className="rounded-full bg-warm-bg px-3 py-1 text-[11px] font-semibold text-text-muted">
          {knownSports ? "Set up once" : `${steps.filter((step) => step.done).length}/${steps.length}`}
        </span>
      </div>
      <ol className="grid gap-2 sm:grid-cols-3">
        {checklistSteps.map((step) => (
          <li
            key={step.id}
            className="rounded-sm border border-border-light bg-warm-bg p-3"
          >
            <div className="flex items-start gap-2">
              <span
                className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                  step.done
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-white text-text-muted"
                }`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold leading-snug text-navy">
                  {knownSports && step.id === "follow-sports" ? "Choose your sports" : step.label}
                </p>
                {knownSports && step.id === "follow-sports" ? (
                  <CompanionSportSetup knownSports={knownSports} includeNamedSupProfiles={includeNamedSupProfiles} />
                ) : !step.done && step.id === "set-base" ? (
                  <button
                    type="button"
                    onClick={() =>
                      window.dispatchEvent(
                        new Event("companion:open-base-sheet"),
                      )
                    }
                    className="mt-2 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-teal px-3 py-2 text-[12px] font-semibold text-white hover:bg-teal-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
                  >
                    {step.ctaLabel}
                  </button>
                ) : !step.done &&
                  step.id === "follow-sports" &&
                  intentSuggestion ? (
                  <IntentSuggestions view={intentSuggestion} />
                ) : !step.done && step.id === "choose-briefing" ? (
                  <button
                    type="button"
                    disabled={briefingPending}
                    onClick={saveInAppOnlyBriefing}
                    className="mt-2 inline-flex min-h-[44px] items-center text-[12px] font-semibold text-gold hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal disabled:cursor-wait disabled:opacity-60"
                  >
                    {briefingPending ? "Saving" : step.ctaLabel}
                  </button>
                ) : (
                  <Link
                    href={step.href}
                    className="mt-2 inline-flex min-h-[44px] items-center text-[12px] font-semibold text-gold no-underline hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
                  >
                    {step.done ? step.statusLabel ?? "Done" : step.ctaLabel}
                  </Link>
                )}
                {step.id === "choose-briefing" && briefingStatus ? (
                  <p
                    role="status"
                    aria-live="polite"
                    className={`mt-2 text-[11.5px] leading-relaxed ${
                      briefingStatus.tone === "error"
                        ? "text-rose-700"
                        : "text-emerald-700"
                    }`}
                  >
                    {briefingStatus.message}
                  </p>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-[12px] leading-relaxed text-text-muted">A Daily Briefing summarises your morning. The Call flags rare conditions at places you follow. Email and push are separate choices.</p>
      <Link href="/companion/settings?filter=availability" className="mt-2 inline-flex min-h-[44px] items-center text-[12px] font-semibold text-teal">Add free time or a travel limit (optional)</Link>
      {confirmation ? (
        <Link
          href={confirmation.href}
          className="mt-4 flex min-h-[44px] items-start gap-3 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-3 no-underline"
        >
          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-[14px] font-semibold text-emerald-950">
              {confirmation.title}
            </span>
            <span className="mt-1 block text-[12.5px] leading-relaxed text-emerald-900">
              {confirmation.body}
            </span>
          </span>
        </Link>
      ) : null}
    </section>
  );
}

function IntentSuggestions({
  view,
}: {
  view: Exclude<CompanionIntentSuggestionView, null>;
}) {
  return view.autoSaveSpots
    ? <IntentAutoSaveSuggestions view={view} />
    : <IntentFollowSuggestions view={view} />;
}

function IntentAutoSaveSuggestions({
  view,
}: {
  view: Exclude<CompanionIntentSuggestionView, null>;
}) {
  const router = useRouter();
  const attemptedRef = useRef(false);
  const [pending, startTransition] = useTransition();
  const [status, setStatus] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);

  const saveSpots = useCallback(() => {
    setStatus(null);
    startTransition(() => {
      void (async () => {
        for (const spot of view.spots) {
          const result = await addFavourite(spot.slug, spot.spotType);
          if (!result.success) {
            setStatus({
              tone: "error",
              message: result.error ?? `Could not save ${spot.name}.`,
            });
            return;
          }
          for (const sport of view.sports) {
            if (!sportMatchesSpotType(sport.sport, spot.spotType)) continue;
            const followResult = await followTheCall(
              spot.slug,
              spot.spotType,
              sport.sport,
            );
            if (!followResult.success) {
              setStatus({
                tone: "error",
                message:
                  followResult.error ?? `Could not follow ${spot.name}.`,
              });
              return;
            }
          }
        }

        setStatus({
          tone: "success",
          message:
            view.sports.length > 0
              ? view.spots.length === 1
                ? `${view.spots[0]?.name ?? "This place"} is saved and your sport is followed.`
                : `${view.spots.length} places are saved and your sports are followed.`
              : view.spots.length === 1
                ? `${view.spots[0]?.name ?? "This place"} is saved to your Companion.`
                : `${view.spots.length} places are saved to your Companion.`,
        });
        completeCompanionSignupIntent(router);
      })();
    });
  }, [router, view]);

  useEffect(() => {
    if (attemptedRef.current) return;
    const timeoutId = window.setTimeout(() => {
      if (attemptedRef.current) return;
      attemptedRef.current = true;
      saveSpots();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [saveSpots]);

  return (
    <section
      aria-label="Save to Companion"
      className="rounded-md border border-gold/30 bg-white p-4 shadow-sm"
    >
      <p className="text-[12px] font-semibold text-navy">Saving your choice</p>
      <p className="mt-1 text-[11.5px] leading-relaxed text-text-muted">
        Finishing the place and sport you chose before signing up. Briefing
        delivery stays off until you switch it on.
      </p>
      {pending && !status ? (
        <p role="status" className="mt-3 text-[12px] font-semibold text-teal-dark">
          Saving to Companion…
        </p>
      ) : null}
      {status ? (
        <p
          role="status"
          aria-live="polite"
          className={`mt-3 text-[12px] leading-relaxed ${
            status.tone === "error" ? "text-rose-700" : "text-emerald-700"
          }`}
        >
          {status.message}
        </p>
      ) : null}
      {status?.tone === "error" ? (
        <button
          type="button"
          disabled={pending}
          onClick={saveSpots}
          className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white disabled:cursor-wait disabled:bg-navy/55"
        >
          {pending ? "Saving" : "Try saving again"}
        </button>
      ) : null}
    </section>
  );
}

function IntentFollowSuggestions({
  view,
}: {
  view: Exclude<CompanionIntentSuggestionView, null>;
}) {
  const router = useRouter();
  const [selectedSpots, setSelectedSpots] = useState<Set<string>>(
    () => new Set(view.spots.map((spot) => spot.slug)),
  );
  const [selectedSports, setSelectedSports] = useState<Set<string>>(
    () => new Set(view.sports.map((sport) => sport.sport)),
  );
  const [pending, startTransition] = useTransition();
  const [status, setStatus] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);

  function toggleSpot(slug: string) {
    setSelectedSpots((current) => {
      const next = new Set(current);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  function toggleSport(sport: string) {
    setSelectedSports((current) => {
      const next = new Set(current);
      if (next.has(sport)) next.delete(sport);
      else next.add(sport);
      return next;
    });
  }

  function applySuggestions() {
    setStatus(null);
    const spots = view.spots.filter((spot) => selectedSpots.has(spot.slug));
    if (spots.length === 0) {
      setStatus({ tone: "error", message: "Choose at least one place to save." });
      return;
    }

    const sports = view.sports
      .filter((sport) => selectedSports.has(sport.sport))
      .map((sport) => sport.sport);

    startTransition(() => {
      void (async () => {
        for (const spot of spots) {
          const sportsForSpot = sports.filter((sport) =>
            sportMatchesSpotType(sport, spot.spotType),
          );

          if (sportsForSpot.length === 0) {
            const result = await addFavourite(spot.slug, spot.spotType);
            if (!result.success) {
              setStatus({
                tone: "error",
                message: result.error ?? `Could not save ${spot.name}.`,
              });
              return;
            }
            continue;
          }

          for (const sport of sportsForSpot) {
            const result = await followTheCall(spot.slug, spot.spotType, sport);
            if (!result.success) {
              setStatus({
                tone: "error",
                message: result.error ?? `Could not follow ${spot.name}.`,
              });
              return;
            }
          }
        }

        setStatus({ tone: "success", message: "Saved to your Companion." });
        completeCompanionSignupIntent(router);
      })();
    });
  }

  return (
    <div className="mt-2 rounded-sm border border-gold/25 bg-white px-3 py-3">
      <p className="text-[12px] font-semibold text-navy">{view.title}</p>
      <p className="mt-1 text-[11.5px] leading-relaxed text-text-muted">
        {view.body}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {view.spots.map((spot) => (
          <label
            key={spot.slug}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy"
          >
            <input
              type="checkbox"
              checked={selectedSpots.has(spot.slug)}
              onChange={() => toggleSpot(spot.slug)}
              className="h-3.5 w-3.5 accent-navy"
            />
            {spot.name}
          </label>
        ))}
        {view.sports.map((sport) => (
          <label
            key={sport.sport}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy"
          >
            <input
              type="checkbox"
              checked={selectedSports.has(sport.sport)}
              onChange={() => toggleSport(sport.sport)}
              className="h-3.5 w-3.5 accent-navy"
            />
            {sportIsBeta(sport.sport)
              ? sport.label.replace(/\s*\(Beta\)$/u, "")
              : sport.label}
            {sportIsBeta(sport.sport) ? <SportBetaBadge /> : null}
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={pending}
        onClick={applySuggestions}
        className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white disabled:cursor-wait disabled:bg-navy/55"
      >
        {pending ? "Saving" : view.applyLabel}
      </button>
      {status ? (
        <p
          role="status"
          aria-live="polite"
          className={`mt-2 text-[11.5px] leading-relaxed ${
            status.tone === "error" ? "text-rose-700" : "text-emerald-700"
          }`}
        >
          {status.message}
        </p>
      ) : null}
    </div>
  );
}

function completeCompanionSignupIntent(
  router: ReturnType<typeof useRouter>,
) {
  clearCompanionSignupIntent(window.localStorage);
  const url = new URL(window.location.href);
  url.searchParams.delete(COMPANION_SIGNUP_INTENT_QUERY_PARAM);
  router.replace(`${url.pathname}${url.search}`, { scroll: false });
  queueCompanionRefresh(router);
}

function sportMatchesSpotType(
  sport: CompanionSignupIntentPrefill["suggestedSports"][number]["sport"],
  spotType: CompanionSignupIntentPrefill["suggestedSpots"][number]["spotType"],
): boolean {
  if (spotType === "ski") {
    return sport === "ski" || sport === "touring" || sport === "mtb";
  }
  if (spotType === "sailing") {
    return (
      sport === "sailing" ||
      sport === "sailing-club" ||
      sport === "sailing-hp"
    );
  }
  return ![
    "ski",
    "touring",
    "mtb",
    "sailing",
    "sailing-club",
    "sailing-hp",
  ].includes(sport);
}
